import { useMemo, useState } from 'react';
import type { PlayerRow, TournamentPlayers } from '@/api/types';
import { dash, formatDateTime, formatDay, formatMoney, formatName } from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { INPUT_CLASS } from '@/ui/Field';
import { Table, Td, Th } from '@/ui/Table';
import { WINDOWS, windowHeading } from './windows';

function matches(player: PlayerRow, needle: string): boolean {
  if (!needle) return true;
  const haystack = [
    player.playerUuid,
    toPlayerId(player.playerUuid),
    player.profile?.username,
    player.profile?.email,
    player.profile?.firstName,
    player.profile?.lastName,
    ...player.agentIds,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

const FIXED_COLUMNS = 12;

export function PlayersTable({ data }: { data: TournamentPlayers }) {
  const [filter, setFilter] = useState('');
  const needle = filter.trim().toLowerCase();
  const rows = useMemo(() => data.players.filter((player) => matches(player, needle)), [data.players, needle]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          type="search"
          aria-label="Filter players"
          className={`${INPUT_CLASS} max-w-sm`}
          placeholder="Filter by player id, username, email or agent"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <p className="text-sm text-slate-500">
          {rows.length} of {data.players.length} players
        </p>
      </div>
      <Table>
        <thead>
          <tr>
            <Th colSpan={FIXED_COLUMNS} />
            {WINDOWS.map((window) => (
              <Th
                key={window.key}
                colSpan={3}
                align="center"
                className={data.windows[window.key].partial ? 'text-amber-700' : ''}
              >
                {windowHeading(data.windows, window.key, window.label)}
              </Th>
            ))}
          </tr>
          <tr>
            <Th align="left">Player ID</Th>
            <Th align="left">Username</Th>
            <Th align="left">Name</Th>
            <Th align="left">Email</Th>
            <Th align="left">Country</Th>
            <Th align="left">Currency</Th>
            <Th align="left">Registered</Th>
            <Th align="left">Agent</Th>
            <Th>Links</Th>
            <Th align="left">Invited</Th>
            <Th align="left">Joined</Th>
            <Th align="left">Last active</Th>
            {WINDOWS.map((window) => [
              <Th key={`${window.key}-active`} align="center">
                Active
              </Th>,
              <Th key={`${window.key}-eur`}>Deposits €</Th>,
              <Th key={`${window.key}-ggr`}>GGR €</Th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td align="left" colSpan={FIXED_COLUMNS + WINDOWS.length * 3} className="text-slate-500">
                {data.players.length === 0
                  ? 'No links have been issued for this tournament.'
                  : 'No player matches the filter.'}
              </Td>
            </tr>
          )}
          {rows.map((player) => (
            <tr key={player.playerUuid}>
              <Td align="left" className="font-mono text-xs">
                {toPlayerId(player.playerUuid)}
              </Td>
              <Td align="left">{player.profile?.username ?? dash}</Td>
              <Td align="left">{formatName(player.profile)}</Td>
              <Td align="left">{player.profile?.email ?? dash}</Td>
              <Td align="left">{player.profile?.country ?? dash}</Td>
              <Td align="left">{player.profile?.currency ?? dash}</Td>
              <Td align="left">{formatDateTime(player.profile?.registrationDate)}</Td>
              <Td align="left">{player.agentIds.join(', ')}</Td>
              <Td>{player.linksIssued}</Td>
              <Td align="left">{formatDateTime(player.invitedAt)}</Td>
              <Td align="left">{formatDateTime(player.redeemedAt)}</Td>
              <Td align="left">{formatDay(player.lastActiveDay)}</Td>
              {WINDOWS.map((window) => {
                const metrics = player.windows?.[window.key];
                return [
                  <Td key={`${window.key}-active`} align="center">
                    {metrics ? (metrics.active ? 'yes' : 'no') : dash}
                  </Td>,
                  <Td key={`${window.key}-eur`}>{metrics ? formatMoney(metrics.depositEur) : dash}</Td>,
                  <Td key={`${window.key}-ggr`}>{metrics ? formatMoney(metrics.ggrEur) : dash}</Td>,
                ];
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
